import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { AuthProvider } from "./contexts/AuthContext";
import { AnalyticsProvider } from "./contexts/AnalyticsContext";
import { SupportProvider } from "./contexts/SupportContext";
import { AnalyticsReporter } from "./components/analytics/AnalyticsReporter";
import { readAnalyticsConfig } from "./lib/analytics/config";
import { disabledAnalytics } from "./lib/analytics/analytics-client";
import { createPostHogAnalytics } from "./lib/analytics/posthog-analytics";
import { createPostHogSdk } from "./lib/analytics/posthog-sdk";
import { unavailableSupport } from "./lib/support/support-client";
import { createPostHogSupport } from "./lib/support/posthog-support";
import {
  reportMutationFailure,
  reportMutationSuccess,
} from "./lib/analytics/mutation-reporting";
import { App } from "./App";
import { WRITE_DEFAULTS } from "./lib/write-defaults";
import { installChunkReload } from "./lib/chunk-reload";
import { hasUnsavedDraft } from "./hooks/use-unsaved-draft";
// The design's mono face, self-hosted. The body face, DM Sans, loads from
// index.html.
import "@fontsource-variable/jetbrains-mono";
import "./index.css";

// Built once, outside render, so StrictMode's double invocation cannot start
// the SDK twice. Analytics and Support share the one SDK. A build with no
// project key gets clients that send nothing, and a feedback send says Support
// is unavailable.
const analyticsConfig = readAnalyticsConfig(import.meta.env);
const posthogSdk = analyticsConfig ? createPostHogSdk(analyticsConfig) : null;
const analytics = posthogSdk
  ? createPostHogAnalytics(posthogSdk)
  : disabledAnalytics;
const support = posthogSdk
  ? createPostHogSupport(posthogSdk, analytics)
  : unavailableSupport;

// Reporting from the cache rather than from each call site: every mutation is
// covered, including the ones a component handles quietly, and no component has
// to remember to say anything.
const mutationCache = new MutationCache({
  onSuccess: (_data, variables, _context, mutation) => {
    reportMutationSuccess(analytics, mutation.options.mutationKey, variables);
  },
  onError: (error, _variables, _context, mutation) => {
    reportMutationFailure(analytics, mutation.options.mutationKey, error);
  },
});

const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
    mutations: WRITE_DEFAULTS,
  },
});

// A tab opened before a deploy reloads once when a chunk of its build is gone,
// unless typing is unsaved; see lib/chunk-reload.ts. Each storage call is made
// inside the helper's own try, since a shut sessionStorage throws on access.
installChunkReload(window, {
  storage: {
    getItem: (key) => window.sessionStorage.getItem(key),
    setItem: (key, value) => {
      window.sessionStorage.setItem(key, value);
    },
  },
  now: () => Date.now(),
  reload: () => {
    window.location.reload();
  },
  unsaved: hasUnsavedDraft,
});

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Missing #root element");
createRoot(rootEl).render(
  <StrictMode>
    <AnalyticsProvider client={analytics}>
      <SupportProvider client={support}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthProvider>
              <AnalyticsReporter />
              <App />
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </SupportProvider>
    </AnalyticsProvider>
  </StrictMode>,
);
